import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { newsList } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";
import MovieCard from "@/components/feature/MovieCard";

const hotKeywords = ["悬疑", "古装", "科幻", "治愈", "刑侦", "年代"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const [input, setInput] = useState(keyword);
  const [tab, setTab] = useState<"movie" | "news">("movie");

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  usePageMeta({
    title: keyword ? `"${keyword}" 搜索结果 - 秋霞影院` : "全站搜索 - 秋霞影院",
    description: keyword
      ? `秋霞影院关于“${keyword}”的搜索结果，包含相关影视作品与影视资讯内容。`
      : "秋霞影院全站搜索，支持影视作品与影视资讯双类别关键词检索。",
    keywords: keyword
      ? `${keyword},影视搜索,秋霞影院`
      : "秋霞影院,影视搜索,影视资讯搜索",
  });

  const movieResults = useMemo(() => {
    if (!keyword) return [];
    const k = keyword.toLowerCase();
    return movies.filter((m) =>
      [m.title, m.region, m.director, ...m.genre, ...m.tags, ...m.cast, m.synopsis]
        .join(" ")
        .toLowerCase()
        .includes(k)
    );
  }, [keyword]);

  const newsResults = useMemo(() => {
    if (!keyword) return [];
    const k = keyword.toLowerCase();
    return newsList.filter((n) =>
      [n.title, n.category, n.summary, ...n.tags, ...n.paragraphs]
        .join(" ")
        .toLowerCase()
        .includes(k)
    );
  }, [keyword]);

  const submit = (value?: string) => {
    const q = (value ?? input).trim();
    if (!q) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q });
  };

  const hasKeyword = keyword.length > 0;
  const total = movieResults.length + newsResults.length;

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <div className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
        <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">全站搜索</h1>
        <p className="mt-1.5 text-sm text-foreground-500">输入关键词，同时检索影视作品与影视资讯</p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="mt-4 flex flex-col gap-3 sm:flex-row"
        >
          <div className="relative flex flex-1 items-center">
            <i className="ri-search-line pointer-events-none absolute left-3.5 text-lg text-foreground-400" />
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="搜索片名、类型、地区、导演或资讯关键词"
              className="h-12 w-full rounded-lg border border-background-300 bg-background-50 pl-11 pr-4 text-sm outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>
          <button
            type="submit"
            className="flex h-12 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-600 px-8 text-sm font-semibold text-white transition-colors hover:bg-primary-700"
          >
            <i className="ri-search-line" /> 搜索
          </button>
        </form>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs text-foreground-400">热门搜索：</span>
          {hotKeywords.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => submit(k)}
              className="cursor-pointer rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600 transition-colors hover:bg-accent-100 hover:text-accent-700"
            >
              {k}
            </button>
          ))}
        </div>
      </div>

      {!hasKeyword ? (
        <div className="mt-6 flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-50 py-20 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
            <i className="ri-search-eye-line" />
          </span>
          <p className="mt-4 font-heading text-base font-semibold text-foreground-800">输入关键词开始搜索</p>
          <p className="mt-1 text-sm text-foreground-500">支持片名、类型、地区、导演以及资讯内容的关键词匹配</p>
        </div>
      ) : (
        <>
          <div className="mt-6 flex flex-wrap items-center gap-2 border-b border-background-200 text-sm">
            <span className="mr-2 text-foreground-500">
              关键词 “<span className="font-semibold text-foreground-900">{keyword}</span>” 共匹配{" "}
              <span className="font-semibold text-accent-600">{total}</span> 条结果
            </span>
            <button
              type="button"
              onClick={() => setTab("movie")}
              className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-t-md px-4 py-2.5 transition-colors ${
                tab === "movie"
                  ? "border-b-2 border-accent-500 font-semibold text-primary-700"
                  : "text-foreground-500 hover:text-foreground-800"
              }`}
            >
              <i className="ri-film-line" /> 影视 {movieResults.length}
            </button>
            <button
              type="button"
              onClick={() => setTab("news")}
              className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-t-md px-4 py-2.5 transition-colors ${
                tab === "news"
                  ? "border-b-2 border-accent-500 font-semibold text-primary-700"
                  : "text-foreground-500 hover:text-foreground-800"
              }`}
            >
              <i className="ri-newspaper-line" /> 资讯 {newsResults.length}
            </button>
          </div>

          <div className="mt-6">
            {tab === "movie" ? (
              movieResults.length > 0 ? (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                  {movieResults.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              ) : (
                <EmptyResult keyword={keyword} tab="影视" />
              )
            ) : newsResults.length > 0 ? (
              <div className="space-y-4">
                {newsResults.map((item) => (
                  <Link
                    key={item.id}
                    to={`/news/${item.id}`}
                    className="group flex cursor-pointer items-start gap-4 rounded-xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                  >
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={`${item.title} - 秋霞影院资讯`}
                      className="h-20 w-28 shrink-0 rounded-lg object-cover object-top"
                    />
                    <div>
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="rounded bg-accent-100 px-2 py-0.5 font-medium text-accent-700">
                          {item.category}
                        </span>
                        <span className="text-foreground-400">{item.date}</span>
                      </div>
                      <h2 className="clamp-2 mt-2 font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                        {item.title}
                      </h2>
                      <p className="clamp-2 mt-1.5 text-sm text-foreground-600">{item.summary}</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <EmptyResult keyword={keyword} tab="资讯" />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function EmptyResult({ keyword, tab }: { keyword: string; tab: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-50 py-16 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
        <i className="ri-emotion-sad-line" />
      </span>
      <p className="mt-4 font-heading text-base font-semibold text-foreground-800">
        未找到与“{keyword}”相关的{tab}内容
      </p>
      <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或返回首页浏览更多内容</p>
      <Link
        to="/"
        className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700"
      >
        返回首页
      </Link>
    </div>
  );
}